import { useState, useEffect } from "react";
import { Plus, Key, Copy, Check, Eye, EyeOff, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API from "@/lib/api";
import { toast } from "sonner";
import { CUSTOMER } from "@/constants/testIds";

export default function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState({});
  const [newKey, setNewKey] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/api-keys");
      setKeys(data.api_keys || []);
    } catch { toast.error("Failed to load API keys"); }
    finally { setLoading(false); }
  };

  const create = async () => {
    if (!label) { toast.error("Enter a label"); return; }
    setSaving(true);
    try {
      const { data } = await API.post("/api-keys", { name: label, permissions: ["sms:send", "contacts:read", "wallet:read"] });
      setNewKey(data.key_secret);
      setModal(false);
      setLabel("");
      load();
    } catch { toast.error("Failed to create API key"); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try { await API.delete(`/api-keys/${id}`); toast.success("Key revoked"); load(); }
    catch { toast.error("Failed to revoke key"); }
  };

  const copy = (id, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied({ ...copied, [id]: true });
      setTimeout(() => setCopied((c) => ({ ...c, [id]: false })), 2000);
    });
  };

  const maskKey = (k) => `${k.substring(0, 12)}${"•".repeat(20)}${k.slice(-4)}`;

  return (
    <div data-testid={CUSTOMER.apiKeysPanel} className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">API Keys</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your API credentials</p>
        </div>
        <Button onClick={() => setModal(true)} size="sm" className="bg-[#0c1e41] hover:bg-[#091629] h-9 shadow-sm" data-testid={CUSTOMER.createApiKeyButton}>
          <Plus size={14} className="mr-2" /> Create API Key
        </Button>
      </div>

      {/* Documentation hint */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
        <p className="font-semibold mb-1 text-slate-900">Quick Start</p>
        <p className="text-xs text-slate-500 mb-3">Use your API key as a Bearer token to authenticate requests.</p>
        <code className="bg-slate-900 text-slate-200 text-xs font-mono px-3 py-2 rounded-lg block">
          Authorization: Bearer sk_live_xxxxxxxxxxxxxxxx...
        </code>
      </div>

      {/* New key banner */}
      {newKey && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4">
          <p className="text-sm font-semibold text-emerald-800 mb-2">New API Key Created — Copy it now!</p>
          <p className="text-xs text-emerald-700 mb-3">This key will not be shown again. Store it securely.</p>
          <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-lg px-3 py-2 font-mono text-xs text-slate-900 break-all">
            <span className="flex-1">{newKey}</span>
            <button onClick={() => copy("new", newKey)} className="text-emerald-700 hover:text-emerald-900 shrink-0">
              {copied["new"] ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          <Button size="sm" variant="ghost" className="mt-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 text-xs" onClick={() => setNewKey(null)}>Dismiss</Button>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-14">
          <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Key size={24} className="text-slate-400" />
          </div>
          <p className="text-sm text-slate-500 mb-4">No API keys yet. Create one to integrate Sendora into your app.</p>
          <Button onClick={() => setModal(true)} className="bg-[#0c1e41] hover:bg-[#091629]">Create First API Key</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((k) => (
            <div key={k._id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Key size={15} className="text-slate-600" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-900">{k.name || k.label}</span>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Created {k.created_at ? new Date(k.created_at).toLocaleDateString("en-NG") : "—"}
                      {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString("en-NG")}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={k.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" : "bg-red-50 text-red-700 border-red-200 text-xs"}>
                    {k.status}
                  </Badge>
                  <button onClick={() => revoke(k._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {k.key_preview && (
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-mono text-xs text-slate-700">
                  <span className="flex-1">{revealed[k._id] ? k.key_preview : maskKey(k.key_preview || "")}</span>
                  <button onClick={() => setRevealed((r) => ({ ...r, [k._id]: !r[k._id] }))} className="text-slate-500 hover:text-slate-700 shrink-0">
                    {revealed[k._id] ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button onClick={() => copy(k._id, k.key_preview || "")} className="text-slate-500 hover:text-slate-700 shrink-0">
                    {copied[k._id] ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                  </button>
                </div>
              )}
              {k.permissions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {k.permissions.map((p) => (
                    <span key={p} className="text-xs bg-[#eef1f8] border border-[#c0cfe8] text-[#0c1e41] px-2 py-0.5 rounded-md font-mono">{p}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Create API Key</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Production Server, Mobile App" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
            </div>
            <Button onClick={create} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629]">Create Key</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
